import { VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
// import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { Utils } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../../hooks/useToastCreator';
import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../../routes/types';
import { useRedemptionActions } from '../../../../store/redemptionSolicitation/actions';
import { directTreasuryWithdrawGetAsset } from '../../../../store/redemptionSolicitation/selector';
import { RedemptionError } from '../../components/RedemptionError';
import { RedemptionHeader } from '../../components/RedemptionHeader';
import { RedemptionSuccess } from '../../components/RedemptionSuccess';
import { defaultError } from '../../constants';

import {
  ConfirmationStepInfos,
  ValueAndConfirmationStep,
} from './components/ValueAndConfirmationStep';
import { ValueAndConfirmationStepLoading } from './components/ValueAndConfirmationStep/loading';

export interface DirectTreasuryRedemptionProps {
  onClose: () => void;
}

export const DirectTreasuryRedemption: React.FC<DirectTreasuryRedemptionProps> = ({ onClose }) => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { showToast } = useToastCreator('direct-treasury-redemption');
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'RedemptionSolicitation'>>();

  const [step, setStep] = useState(1);
  const [isExecuting, setIsExecuting] = useState(false);
  const [extraData, setExtraData] = useState<ConfirmationStepInfos>();

  const { executeDirectTreasuryRedemption } = useRedemptionActions();

  const { data, error, refresher, loading } = useRecoilQuery(
    directTreasuryWithdrawGetAsset({
      cpfCnpj: params.cpfCnpjFormatted || params.cpfCnpj.toString(),
      productId: params.productId,
    }),
    { cachePolicy: 'no-cache' }
  );

  const handleExecuteRedemption = (infos: ConfirmationStepInfos) => {
    if (!data) {
      return;
    }
    setExtraData(infos);
    setIsExecuting(true);
    executeDirectTreasuryRedemption({
      accountId: params.accountId,
      financialValue: infos.totalValue,
      quantity: infos.quantityToRedeem,
      titleCode: data.titleCode,
      interactionChannel: infos.channel,
      observation: infos.observation,
      cpf: params.cpfCnpjFormatted,
    })
      .then(({ financialValue, quantity }) => {
        setExtraData({
          ...infos,
          totalValue: financialValue,
          quantityToRedeem: quantity,
        });
        setStep(step + 1);
      })
      .catch(() => {
        // if (instanceOfErrorListResponse(executeError)) {
        //   (executeError as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
        //     showToastFromError(errorItem)
        //   );
        //   return;
        // }
        showToast(...defaultError);
      })
      .then(() => setIsExecuting(false));
  };

  const handlePressBack = () => {
    if (step === 2) {
      return setStep(1);
    }
    onClose();
  };

  const handleFollowRedemption = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
    navigation.navigate('AppDrawer', {
      screen: 'CustomerDetailGroup',
      params: {
        screen: 'CustomerDetail',
        params: {
          ...params,
          selectedTab: 'transactions',
          movementInitialTab: 'Treasury',
        },
      },
    });
  };

  const handleRefresh = () => {
    refresher();
    setStep(1);
  };

  const renderStep = () => {
    if (step === 99) {
      return (
        <RedemptionError
          onRefresh={handleRefresh}
          errorMessage="No momento não conseguimos te mostrar os resultados. Tente novamente em alguns instantes"
        />
      );
    }
    if (loading || !data) {
      return <ValueAndConfirmationStepLoading productName={params.productName} />;
    }

    switch (step) {
      case 1:
        return (
          <ValueAndConfirmationStep
            handleCancel={onClose}
            handleConfirm={handleExecuteRedemption}
            treasury={data}
            isExecuting={isExecuting}
          />
        );

      case 2:
        return (
          <RedemptionSuccess
            infos={[
              { label: 'Quantidade', value: extraData?.quantityToRedeem?.toString() },
              { label: 'Valor estimado', value: Utils.Mask.Money(extraData?.totalValue) },
              { label: 'Data resgate', value: Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy') },
            ]}
            onClose={onClose}
            onFollowRedemption={handleFollowRedemption}
          />
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    if (error) {
      setStep(99);
    }
  }, [error]);

  return (
    <VStack space={Spacing['stack.xxxs']}>
      <RedemptionHeader onClose={handlePressBack} />
      {renderStep()}
    </VStack>
  );
};
